/**
 * Re-applying queued intent over cached data, and the small helpers every
 * outbox transaction shares.
 *
 * A policy's `applyLocally` is idempotent by contract, so the cache can be
 * rebuilt at any time as "server data, then every queued row's echo in seq
 * order". Used at enqueue, after a replay, and when a read lands for an
 * entity that still has rows queued.
 */

import { writePolicyFor } from './writePolicies';

import type { OfflineTx, OfflineTxMode, OutboxRow } from './store';
import type { LocalStore, WriteRequest } from './writePolicies';

/** Meta key holding the revision threaded from the last acknowledged reply for an entity. */
export const revisionKey = (entityKey: string): string => `rev:${entityKey}`;

/** A LocalStore over the `resources` store of an open transaction; writes throw on a read-only one. */
export function localStoreOf(tx: OfflineTx<OfflineTxMode>): LocalStore {
  const resources = tx.objectStore('resources');
  return {
    async get<T>(key: string) {
      return ((await resources.get(key))?.data as T | undefined) ?? null;
    },
    async put<T>(key: string, data: T) {
      await (resources as { put: (v: unknown) => Promise<unknown> }).put({
        key,
        data,
        fetchedAt: Date.now(),
      });
    },
    async delete(key: string) {
      await (resources as { delete: (k: string) => Promise<unknown> }).delete(key);
    },
  };
}

/** Apply each row's echo in seq order, inside the given transaction. Rows without a policy are skipped. */
export async function applyEchoes(tx: OfflineTx<'readwrite'>, rows: OutboxRow[]): Promise<void> {
  const store = localStoreOf(tx);
  for (const row of [...rows].sort((a, b) => a.seq - b.seq)) {
    const req: WriteRequest = { method: row.method, url: row.path, body: row.body };
    // Bound to a local first: `await policy?.applyLocally?.(…)` awaits
    // `undefined` for a row whose policy has no echo, and an await with no
    // store request pending lets IndexedDB commit the transaction early.
    const apply = writePolicyFor(req)?.applyLocally;
    if (apply) await apply(store, req);
  }
}

/**
 * Rows the outbox holds for one entity, read inside the transaction, seq order.
 *
 * "Queued" names where they live, not their `state`: every row for the
 * entity comes back, `inflight` and `failed` included. A caller that cares
 * about one state filters for it.
 */
export async function queuedRowsIn(
  tx: OfflineTx<OfflineTxMode>,
  entityKey: string
): Promise<OutboxRow[]> {
  const rows = await tx.objectStore('outbox').index('byEntity').getAll(entityKey);
  return rows.sort((a, b) => a.seq - b.seq);
}
